import {
  ContainersList,
  MainContainer,
  SubTitle,
} from "./BoardInventoryStyles";
import React, { Dispatch, SetStateAction, useState } from "react";
import { PartWithInventory } from "../../../utils/hooks/inventory/boardInventory";
import BoardInventoryContainerInfo from "./ContainerInfo";
import Modal from "../../common/Modal";
import ModalContentWithCloseButton from "../../common/ModalContentWithCloseButton";
import AddNewContainer from "../../component-search/PartDrawer/Inventory/PhysicalInventory/AddNewContainer";
import {
  Container,
  InventoryResponse,
} from "../../../reducers/componentSearch";
import { FakeLink } from "../../../constants/Styles";
import { useUser } from "../../../utils/hooks/commonly-stored/users";
import { InventoryBin } from "../parts/Bins/inventoryBinTypes";
import Checkbox from "../../common/Checkbox";

export const allocationQuantitiesByContainer = (
  part: PartWithInventory,
  shipmentId: string,
): Record<string, number> => {
  const allocationMap: Record<string, number> = {};

  for (const container of part.inventory_response.physical_inventory
    .containers) {
    let totalAllocated = 0;

    for (const allocation of container.part_allocations) {
      if (allocation.shipment_id === shipmentId) {
        totalAllocated += allocation.quantity_allocated;
      }
    }

    if (totalAllocated > 0) {
      allocationMap[container.flashpcb_container_barcode] = totalAllocated;
    }
  }

  return allocationMap;
};

export default function PartContainers({
  part,
  boardBin,
  shipmentId,
  boardUserId,
  selectedContainers,
  setSelectedContainers,
}: {
  part: PartWithInventory;
  boardBin: InventoryBin | null;
  shipmentId: string;
  boardUserId: string | null;
  selectedContainers: Container[];
  setSelectedContainers: Dispatch<SetStateAction<Container[]>>;
}) {
  const [addContainer, setAddContainer] = useState<boolean>(false);
  const [selectedPart, setSelectedPart] = useState<PartWithInventory | null>(
    null,
  );
  const [selectedPartInventoryResponse, setSelectedPartInventoryResponse] =
    useState<InventoryResponse>();
  const [showContainersInInventory, setShowContainersInInventory] =
    useState<boolean>(false);
  const { user } = useUser(boardUserId || null);

  const closeModal = () => {
    setAddContainer(false);
  };

  const onFinishedEditing = () => {
    closeModal();
  };

  const createNewContainer = (part: PartWithInventory) => {
    setSelectedPart(part);
    if (part.part && part.inventory_response) {
      setSelectedPartInventoryResponse(part.inventory_response);
    }
    setAddContainer(true);
  };
  const nonReservedContainers =
    part.inventory_response.physical_inventory.containers.filter(
      (container) =>
        !container.consignment_container_owner_id ||
        container.consignment_container_owner_id === boardUserId,
    );
  const allocationsForPart = allocationQuantitiesByContainer(part, shipmentId);

  const containersAllocatedOrInTheBoardTote = nonReservedContainers.filter(
    (container: Container) => {
      const allocation =
        allocationsForPart[container.flashpcb_container_barcode];
      const isAllocated = allocation !== undefined && allocation > 0;
      const isInBoardTote =
        container.flashpcb_bin_barcode === boardBin?.flashpcb_bin_barcode;
      return isAllocated || isInBoardTote;
    },
  );

  const containersInInventory = nonReservedContainers.filter((container) => {
    return (
      !allocationsForPart[container.flashpcb_container_barcode] &&
      container.last_counted_amount > 0 &&
      container.flashpcb_bin_barcode !== boardBin?.flashpcb_bin_barcode
    );
  });

  const otherAvailableInventory = containersInInventory.reduce(
    (availableInventory, container) => {
      availableInventory += container.available_inventory_level;
      return availableInventory;
    },
    0,
  );

  return (
    <MainContainer>
      {containersAllocatedOrInTheBoardTote.length > 0 && (
        <ContainersList>
          <SubTitle>Allocated and / or pulled containers:</SubTitle>
          {containersAllocatedOrInTheBoardTote.map((container) => {
            const qtyAllocated =
              allocationsForPart[container.flashpcb_container_barcode] || 0;
            return (
              <BoardInventoryContainerInfo
                key={container.flashpcb_container_barcode}
                part={part}
                container={container}
                qtyAllocated={qtyAllocated}
                requiredAmount={part.required_amount}
                boardBin={boardBin}
                shipmentId={shipmentId}
                selectedContainers={selectedContainers}
                setSelectedContainers={setSelectedContainers}
              />
            );
          })}
        </ContainersList>
      )}
      {containersInInventory.length > 0 && (
        <Checkbox
          label={
            part.part.is_updating
              ? "Show other containers (Available quantity: updating...)"
              : `Show other containers (Available quantity: ${otherAvailableInventory})`
          }
          checked={showContainersInInventory}
          onChange={() =>
            setShowContainersInInventory(!showContainersInInventory)
          }
        />
      )}
      {containersInInventory.length > 0 && showContainersInInventory && (
        <ContainersList>
          <SubTitle>Other containers:</SubTitle>
          {containersInInventory.map((container) => {
            return (
              <BoardInventoryContainerInfo
                key={container.flashpcb_container_barcode}
                part={part}
                container={container}
                qtyAllocated={0}
                requiredAmount={part.required_amount}
                boardBin={boardBin}
                shipmentId={shipmentId}
                selectedContainers={selectedContainers}
                setSelectedContainers={setSelectedContainers}
              />
            );
          })}
        </ContainersList>
      )}
      <FakeLink onClick={() => createNewContainer(part)}>
        Add container
      </FakeLink>
      {selectedPart && (
        <Modal isOpen={addContainer} onRequestClose={closeModal}>
          <ModalContentWithCloseButton closeModal={closeModal} fullScreen>
            <AddNewContainer
              partId={selectedPart.part.id as string}
              part={selectedPart.part}
              onFinishedEditing={onFinishedEditing}
              onCancel={closeModal}
              inventoryResponse={selectedPartInventoryResponse}
              inventoryBin={boardBin || undefined}
              user={selectedPart.is_consignment && user ? user : null}
            />
          </ModalContentWithCloseButton>
        </Modal>
      )}
    </MainContainer>
  );
}
